/**
 * The room event protocol. One event bus carries every message type, so audio,
 * transcript, scroll and touch events all share a single authenticated path.
 *
 * Control messages are JSON in a versioned envelope. Audio is the only
 * exception: it travels as binary frames (see ./chunk.ts).
 */
import { z } from 'zod';

export const PROTOCOL_VERSION = 1;

/**
 * `relay` is the control plane itself. No token is ever issued for it and
 * {@link ALLOWED_SENDERS} grants it nothing, so it exists only to label
 * server-authored events honestly.
 */
export const roleSchema = z.enum(['agent', 'controller', 'viewer', 'worker', 'relay']);
export type Role = z.infer<typeof roleSchema>;

/** Roles a session token may be minted for. */
export type IssuableRole = Exclude<Role, 'relay'>;

export const audioChannelSchema = z.enum(['mic', 'system']);

/** `you` is always the mic channel; `remote-1`..`remote-4` come from the diarizer. */
export const speakerKeySchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9-]*$/, 'speaker keys are lowercase kebab-case');

export const MAX_REMOTE_SPEAKERS = 4;
export const SELF_SPEAKER_KEY = 'you';

/** Below this the UI shows the label as a draft rather than a fact. */
export const LOW_CONFIDENCE_THRESHOLD = 0.6;

export const sessionStatusSchema = z.enum(['starting', 'live', 'ended']);
export type SessionStatus = z.infer<typeof sessionStatusSchema>;

export const workerStatusSchema = z.enum(['absent', 'provisioning', 'ready', 'failed']);
export type WorkerStatus = z.infer<typeof workerStatusSchema>;

export const speakerSchema = z.object({
  key: speakerKeySchema,
  displayName: z.string().min(1).max(120),
  /** Diarizer confidence for the most recent assignment, or null for `you`. */
  confidence: z.number().min(0).max(1).nullable(),
});
export type Speaker = z.infer<typeof speakerSchema>;

export const segmentSchema = z.object({
  /** Stable identity assigned by the producer. Partials, finals and later corrections share it. */
  id: z.string().min(1).max(128),
  /** Store-assigned monotonic order key within the session. Catch-up and sorting use this. */
  seq: z.number().int().nonnegative(),
  sessionId: z.string().min(1),
  channel: audioChannelSchema,
  startMicros: z.number().int().nonnegative(),
  endMicros: z.number().int().nonnegative(),
  text: z.string(),
  speakerKey: speakerKeySchema,
  confidence: z.number().min(0).max(1),
  isFinal: z.boolean(),
  /** Bumped on every correction. Clients drop updates older than what they hold. */
  revision: z.number().int().nonnegative(),
});
export type Segment = z.infer<typeof segmentSchema>;

/** A partial correction to an already-delivered segment. */
export const segmentPatchSchema = z.object({
  id: z.string().min(1).max(128),
  revision: z.number().int().nonnegative(),
  text: z.string().optional(),
  speakerKey: speakerKeySchema.optional(),
  confidence: z.number().min(0).max(1).optional(),
  isFinal: z.boolean().optional(),
});
export type SegmentPatch = z.infer<typeof segmentPatchSchema>;

/**
 * Where the controller is looking: a segment plus how far through it, never a
 * pixel offset. Viewers with different screens resolve it against their own layout.
 */
export const scrollAnchorSchema = z.object({
  segmentId: z.string().min(1).max(128),
  fraction: z.number().min(0).max(1),
  /** Sender clock in epoch ms, for the end-to-end latency log. */
  sentAt: z.number().int().nonnegative(),
});
export type ScrollAnchor = z.infer<typeof scrollAnchorSchema>;

/** Touch normalised to the transcript column, so it lands on the same word everywhere. */
export const touchPointSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  state: z.enum(['down', 'move', 'up']),
  /** Anchors the vertical position to content rather than viewport, as the column scrolls. */
  segmentId: z.string().min(1).max(128).nullable(),
  sentAt: z.number().int().nonnegative(),
});
export type TouchPoint = z.infer<typeof touchPointSchema>;

const payloads = {
  /** First frame on every socket. The socket is closed if it is not a valid hello. */
  hello: z.object({
    token: z.string().min(1),
    role: roleSchema,
    clientId: z.string().min(1).max(64),
    /** Viewers resume from here; the relay replies with a catchup for everything after. */
    lastSegmentSeq: z.number().int().nonnegative().nullable().optional(),
  }),
  welcome: z.object({
    sessionId: z.string().min(1),
    clientId: z.string().min(1),
    role: roleSchema,
    status: sessionStatusSchema,
    workerStatus: workerStatusSchema,
    serverTime: z.number().int().nonnegative(),
    /** When the meeting started, epoch ms; null until the agent attaches. Dates a past meeting. */
    startedAt: z.number().int().nonnegative().nullable().optional(),
  }),
  'session.start': z.object({
    sessionId: z.string().min(1),
    language: z.literal('en'),
    startedAt: z.number().int().nonnegative(),
  }),
  'session.end': z.object({
    sessionId: z.string().min(1),
    reason: z.enum(['user', 'idle', 'error']),
  }),
  'session.state': z.object({
    sessionId: z.string().min(1),
    status: sessionStatusSchema,
    workerStatus: workerStatusSchema,
    speakers: z.array(speakerSchema),
    viewerCount: z.number().int().nonnegative(),
  }),
  'transcript.segment': z.object({ segment: segmentSchema }),
  'transcript.update': z.object({ patches: z.array(segmentPatchSchema).min(1) }),
  'speaker.rename': z.object({
    speakerKey: speakerKeySchema,
    displayName: z.string().min(1).max(120),
  }),
  'scroll.anchor': scrollAnchorSchema,
  'touch.point': touchPointSchema,
  /** Highest contiguous sequence the relay has durably forwarded, per channel. */
  'audio.ack': z.object({
    channel: audioChannelSchema,
    seq: z.number().int().min(-1),
  }),
  catchup: z.object({
    segments: z.array(segmentSchema),
    speakers: z.array(speakerSchema),
    /** False when the page was capped; the client asks again from the last seq it got. */
    complete: z.boolean(),
  }),
  error: z.object({
    code: z.enum([
      'unauthorized',
      'bad_request',
      'session_not_found',
      'session_ended',
      'rate_limited',
      'internal',
    ]),
    message: z.string(),
  }),
} as const;

export type EventType = keyof typeof payloads;
export const EVENT_TYPES = Object.keys(payloads) as EventType[];

export const eventTypeSchema = z.enum(EVENT_TYPES as [EventType, ...EventType[]]);

const envelopeBase = {
  v: z.literal(PROTOCOL_VERSION),
  id: z.string().min(1).max(64),
  /** Producer wall clock in epoch ms. Informational; ordering comes from seq. */
  ts: z.number().int().nonnegative(),
  room: z.string().min(1).max(128),
  from: z.object({ role: roleSchema, clientId: z.string().min(1).max(64) }),
};

/**
 * Validates envelope shape only. `decodeEvent` then validates the payload with
 * the schema for the parsed `type`, which keeps the exported types hand-written
 * and precise instead of inferred from a 14-member union.
 */
export const envelopeSchema = z.object({
  ...envelopeBase,
  type: eventTypeSchema,
  payload: z.unknown(),
});

export type Payloads = { [K in EventType]: z.infer<(typeof payloads)[K]> };

export type EventOf<K extends EventType> = {
  v: typeof PROTOCOL_VERSION;
  id: string;
  ts: number;
  room: string;
  from: { role: Role; clientId: string };
  type: K;
  payload: Payloads[K];
};

export type Envelope = { [K in EventType]: EventOf<K> }[EventType];

export function payloadSchemaFor<K extends EventType>(type: K): (typeof payloads)[K] {
  return payloads[type];
}

let counter = 0;
/** Envelope ids only need to be unique per producer; they are not ordering keys. */
export function nextEventId(prefix = 'e'): string {
  counter = (counter + 1) % 1_000_000;
  return `${prefix}-${Date.now().toString(36)}-${counter.toString(36)}`;
}

export function makeEvent<K extends EventType>(
  type: K,
  room: string,
  from: { role: Role; clientId: string },
  payload: Payloads[K],
): EventOf<K> {
  return { v: PROTOCOL_VERSION, id: nextEventId(), ts: Date.now(), room, from, type, payload };
}

export class ProtocolError extends Error {
  override readonly name = 'ProtocolError';
}

export function encodeEvent(event: Envelope): string {
  return JSON.stringify(event);
}

export function decodeEvent(raw: string): Envelope {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    throw new ProtocolError('event is not valid JSON');
  }
  const shape = envelopeSchema.safeParse(json);
  if (!shape.success) {
    const issue = shape.error.issues[0];
    throw new ProtocolError(
      `invalid envelope: ${issue ? `${issue.path.join('.') || 'root'}: ${issue.message}` : 'unknown'}`,
    );
  }
  const body = payloadSchemaFor(shape.data.type).safeParse(shape.data.payload);
  if (!body.success) {
    const issue = body.error.issues[0];
    throw new ProtocolError(
      `invalid ${shape.data.type} payload: ${issue ? `${issue.path.join('.') || 'root'}: ${issue.message}` : 'unknown'}`,
    );
  }
  return { ...shape.data, payload: body.data } as Envelope;
}

export function isEventOf<K extends EventType>(
  event: Envelope,
  type: K,
): event is Extract<Envelope, { type: K }> {
  return event.type === type;
}

/** Who is allowed to originate each event type. The relay rejects anything else. */
export const ALLOWED_SENDERS: Record<EventType, readonly Role[]> = {
  hello: ['agent', 'controller', 'viewer', 'worker'],
  welcome: [],
  'session.start': ['agent'],
  'session.end': ['agent', 'controller'],
  'session.state': [],
  'transcript.segment': ['worker'],
  'transcript.update': ['worker'],
  // The agent is here so it can publish the names it reads out of the meeting
  // window the moment it reads them, which is what makes them appear in every
  // viewer with no further work.
  'speaker.rename': ['controller', 'viewer', 'agent'],
  'scroll.anchor': ['controller'],
  'touch.point': ['controller'],
  'audio.ack': [],
  catchup: [],
  error: [],
};

export function canSend(role: Role, type: EventType): boolean {
  return ALLOWED_SENDERS[type].includes(role);
}
